'use client'

import Image from "next/image";
import Button from "@/components/ui/button";
import Link from "@/components/ui/Link";
import { getReadableImageUrl } from "@/lib/directus";
import { sanitizeHTML } from "@/lib/utils";
import { useState } from "react";

interface AboutSectionRigthVideoData {
  anchor_title?: string;
  button?: {
    href?: string;
    label?: string;
  };
  heading?: {
    text?: string;
    title?: string;
  };
  subheading?: {
    text?: string;
    title?: string;
  };
  text?: {
    text?: string;
  };
  image?: {
    alt?: string;
    image_file?: {
      image?: {
        id?: string;
        filename_disk?: string;
      };
    };
  };
  video?: {
		embed_url?: string
		embed_title?: string
		photo?: {
			id?: string
			filename_disk?: string
		}
	}
}

interface AboutSectionRigthVideoProps {
  data?: AboutSectionRigthVideoData;
}

export const fields = [
	'button.href',
	'button.label',

	'heading.text',
	'heading.title',

	'subheading.text',
	'subheading.title',

	'text.text',

	'image.alt',
	'image.image_file.image.id',
	'image.image_file.image.filename_download',

	'video.embed_url',
	'video.embed_title',
	'video.photo.id',
	'video.photo.filename_disk',
	'anchor_title',
] as const

// Helper functions for YouTube handling (support full URL or plain ID)
const getYouTubeThumbnailUrl = (
	videoId: string,
	quality: 'default' | 'hq' | 'maxres' = 'maxres'
): string => {
	if (quality === 'default') {
		return `https://img.youtube.com/vi/${videoId}/0.jpg`
	} else if (quality === 'hq') {
		return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
	} else {
		return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`
	}
}

const getVideoIdFromEmbedUrl = (embedUrl: string): string | null => {
	// If it's already just an ID (no slashes and no http), return it
	if (!embedUrl.includes('/') && !embedUrl.includes('http')) {
		return embedUrl
	}
	// Try to extract from typical YouTube embed URL formats
	const embedMatch = embedUrl.match(/\/embed\/([^?]+)/)
	if (embedMatch) return embedMatch[1]
	// Fallbacks for watch URLs and short youtu.be
	const watchMatch = embedUrl.match(/[?&]v=([^&]+)/)
	if (watchMatch) return watchMatch[1]
	const shortMatch = embedUrl.match(/youtu\.be\/([^?]+)/)
	return shortMatch ? shortMatch[1] : null
}

const getFullEmbedUrl = (embedUrl: string): string => {
	if (embedUrl.includes('http')) {
		return embedUrl
	}
	return `https://www.youtube.com/embed/${embedUrl}`
}

const isDirectVideoUrl = (url: string): boolean => {
	return /\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(url)
}

export default function AboutSectionRigthVideo({ data }: AboutSectionRigthVideoProps) {
	const [isPlaying, setIsPlaying] = useState(false)
	const embedUrl = data?.video?.embed_url ? getFullEmbedUrl(data.video.embed_url) : ''
	const videoId = data?.video?.embed_url ? getVideoIdFromEmbedUrl(data.video.embed_url) : null
	const isDirect = data?.video?.embed_url ? isDirectVideoUrl(data.video.embed_url) : false
	const videoThumbnailUrl =
		data?.video?.photo?.id && data?.video?.photo?.filename_disk
			? getReadableImageUrl(data.video.photo.id, data.video.photo.filename_disk)
			: videoId
				? getYouTubeThumbnailUrl(videoId)
				: ''
	const sectionIdBase = data?.anchor_title || 'AboutSectionRigthVideo'
	const sectionId = sectionIdBase
		.replace(/\s+/g, '-')
		.replaceAll(/[^a-zA-Z0-9-_]/g, '-')

  return (
		<section id={sectionId} data-name={data?.anchor_title || 'AboutSectionRigthVideo'} className='py-16'>
			<div className='container'>
				<div className='flex flex-col lg:flex-row lg:space-x-8 mx-auto justify-center md:gap-16'>
					<div className='order-2 lg:order-1 w-full lg:w-1/2 mb-8 lg:mb-0 relative flex flex-wrap content-between'>
						<div>
							{data?.heading && (
								<h2>{data.heading.title || data.heading.text}</h2>
							)}
							{data?.text && (
								<div
									className='grid gap-6'
									dangerouslySetInnerHTML={sanitizeHTML(data.text.text || '')}
								/>
							)}
						</div>
						<div className='w-full'>
							{data?.button && (
								<div className='mt-6 text-left w-full'>
									<Link className='btn' href={data.button.href || ''}>
										{data.button.label}
									</Link>
								</div>
							)}
						</div>
					</div>
					<div className='order-1 lg:order-2 w-full lg:w-1/2 mb-8 lg:mb-0'>
						{data?.subheading && (
							<div className='font-aileron font-bold text-xs uppercase tracking-wider text-secondary-dark mb-4'>
								{data.subheading.title || data.subheading.text}
							</div>
						)}
					{data?.video && (
						<div className='relative w-full rounded-[18px] overflow-hidden'>
							<div className='aspect-video relative'>
								{isDirect ? (
									videoThumbnailUrl && !isPlaying ? (
										<div
											className='relative w-full h-full cursor-pointer group'
											onClick={() => setIsPlaying(true)}
										>
											<Image
												src={videoThumbnailUrl}
												alt={data.video.embed_title || 'Video thumbnail'}
												fill
												className='object-cover group-hover:scale-105 transition-transform duration-300'
												unoptimized
												priority={false}
											/>
											<div className='absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors'>
												<div className='group-hover:scale-110 transition-all duration-300'>
													<svg
														width='68'
														height='48'
														viewBox='0 0 68 48'
														className='drop-shadow-lg md:w-[68px] w-[40px]'
													>
														<path
															className='fill-white/90 group-hover:fill-white'
															d='M66.52,7.74c-0.78-2.93-2.49-5.41-5.42-6.19C55.79,.13,34,0,34,0S12.21,.13,6.9,1.55 C3.97,2.33,2.27,4.81,1.48,7.74C0.06,13.05,0,24,0,24s0.06,10.95,1.48,16.26c0.78,2.93,2.49,5.41,5.42,6.19 C12.21,47.87,34,48,34,48s21.79-0.13,27.1-1.55c2.93-0.78,4.64-3.26,5.42-6.19C67.94,34.95,68,24,68,24S67.94,13.05,66.52,7.74z'
														/>
														<path className='fill-black/60' d='M 45,24 27,14 27,34' />
													</svg>
												</div>
											</div>
										</div>
								) : (
									<video
										src={embedUrl}
										title={data.video.embed_title}
										className='w-full h-full absolute inset-0 object-contain bg-black'
										controls
										autoPlay={isPlaying}
										playsInline
									/>
								)
								) : videoId ? (
									isPlaying ? (
										<iframe
											src={`${embedUrl}?autoplay=1`}
											title={data.video.embed_title}
											className='w-full h-full absolute inset-0'
											frameBorder='0'
											allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
											allowFullScreen
										/>
									) : (
										<div
											className='relative w-full h-full cursor-pointer group'
											onClick={() => setIsPlaying(true)}
										>
											<Image
												src={videoThumbnailUrl || getYouTubeThumbnailUrl(videoId)}
												alt={data.video.embed_title || 'Video thumbnail'}
												fill
												className='object-cover group-hover:scale-105 transition-transform duration-300'
												unoptimized
												priority={false}
											/>
											<div className='absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors'>
												<div className='group-hover:scale-110 transition-all duration-300'>
													<svg
														width='68'
														height='48'
														viewBox='0 0 68 48'
														className='drop-shadow-lg md:w-[68px] w-[40px]'
													>
														<path
															className='fill-red-600 group-hover:fill-red-700'
															d='M66.52,7.74c-0.78-2.93-2.49-5.41-5.42-6.19C55.79,.13,34,0,34,0S12.21,.13,6.9,1.55 C3.97,2.33,2.27,4.81,1.48,7.74C0.06,13.05,0,24,0,24s0.06,10.95,1.48,16.26c0.78,2.93,2.49,5.41,5.42,6.19 C12.21,47.87,34,48,34,48s21.79-0.13,27.1-1.55c2.93-0.78,4.64-3.26,5.42-6.19C67.94,34.95,68,24,68,24S67.94,13.05,66.52,7.74z'
														/>
														<path className='fill-white' d='M 45,24 27,14 27,34' />
													</svg>
												</div>
											</div>
										</div>
									)
								) : (
									<iframe
										src={embedUrl}
										title={data.video.embed_title}
										className='w-full h-full absolute inset-0'
										frameBorder='0'
										allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
										allowFullScreen
									/>
								)}
							</div>
						</div>
					)}
						{data?.image && (
							<div className='relative w-full max-w-[600px] rounded-[18px] overflow-hidden'>
								<Image
									src={getReadableImageUrl(
										data.image.image_file?.image?.id || '',
										data.image.image_file?.image?.filename_disk || ''
									)}
									alt={data.image.alt || ''}
									width={980}
									height={653}
									quality={95}
									loading={'lazy'}
								/>
							</div>
						)}
					</div>
				</div>
			</div>
		</section>
	)
}
